/**
 * Color Design Tokens
 * Based on Figma Design System - Vasit Admin
 * Synced from Figma Variables
 */

// White color palette (opacity percentages)
export const white = {
  1: 'rgba(255, 255, 255, 0.01)',
  3: 'rgba(255, 255, 255, 0.03)',
  5: 'rgba(255, 255, 255, 0.05)',
  10: 'rgba(255, 255, 255, 0.10)',
  15: 'rgba(255, 255, 255, 0.15)',
  20: 'rgba(255, 255, 255, 0.20)',
  30: 'rgba(255, 255, 255, 0.30)',
  40: 'rgba(255, 255, 255, 0.40)',
  50: 'rgba(255, 255, 255, 0.50)',
  60: 'rgba(255, 255, 255, 0.60)',
  70: 'rgba(255, 255, 255, 0.70)',
  80: 'rgba(255, 255, 255, 0.80)',
  90: 'rgba(255, 255, 255, 0.90)',
  100: '#FFFFFF',
} as const;

// Brand color palette (Primary)
export const brand = {
  25: '#F2F7FF',
  50: '#ECF3FF',
  100: '#DDE9FF',
  200: '#C2D6FF',
  300: '#9CB9FF',
  400: '#7592FF',
  500: '#465FFF',
  600: '#3641F5',
  700: '#2A31D8',
  800: '#252DAE',
  900: '#262E89',
  950: '#161950',
} as const;

// Gray color palette
export const gray = {
  25: '#FCFCFD',
  50: '#F9FAFB',
  100: '#F2F4F7',
  200: '#E4E7EC',
  300: '#D0D5DD',
  400: '#98A2B3',
  500: '#667085',
  600: '#475467',
  700: '#344054',
  800: '#1D2939',
  900: '#101828',
  950: '#0C111D',
} as const;

// Error/Red color palette
export const error = {
  25: '#FFFBFA',
  50: '#FEF3F2',
  100: '#FEE4E2',
  200: '#FECDCA',
  300: '#FDA29B',
  400: '#F97066',
  500: '#F04438',
  600: '#D92D20',
  700: '#B42318',
  800: '#912018',
  900: '#7A271A',
  950: '#55160C',
} as const;

// Warning/Yellow color palette
export const warning = {
  25: '#FFFCF5',
  50: '#FFFAEB',
  100: '#FEF0C7',
  200: '#FEDF89',
  300: '#FEC84B',
  400: '#FDB022',
  500: '#F79009',
  600: '#DC6803',
  700: '#B54708',
  800: '#93370D',
  900: '#7A2E0E',
  950: '#4E1D09',
} as const;

// Success/Green color palette
export const success = {
  25: '#F6FEF9',
  50: '#ECFDF3',
  100: '#D1FADF',
  200: '#A6F4C5',
  300: '#6CE9A6',
  400: '#32D583',
  500: '#12B76A',
  600: '#039855',
  700: '#027A48',
  800: '#05603A',
  900: '#054F31',
  950: '#053321',
} as const;

// Blue color palette
export const blue = {
  25: '#F5FAFF',
  50: '#EFF8FF',
  100: '#D1E9FF',
  200: '#B2DDFF',
  300: '#84CAFF',
  400: '#53B1FD',
  500: '#2E90FA',
  600: '#1570EF',
  700: '#175CD3',
  800: '#1849A9',
  900: '#194185',
  950: '#102A56',
} as const;

// Blue Light color palette
export const blueLight = {
  25: '#F5FBFF',
  50: '#F0F9FF',
  100: '#E0F2FE',
  200: '#B9E6FE',
  300: '#7CD4FD',
  400: '#36BFFA',
  500: '#0BA5EC',
  600: '#0086C9',
  700: '#026AA2',
  800: '#065986',
  900: '#0B4A6F',
  950: '#062C41',
} as const;

// Blue Gray color palette
export const blueGray = {
  25: '#FCFCFD',
  50: '#F8F9FC',
  100: '#EAECF5',
  200: '#C8CCE5',
  300: '#9EA5D1',
  400: '#717BBC',
  500: '#4E5BA6',
  600: '#3E4784',
  700: '#363F72',
  800: '#293056',
  900: '#101323',
  950: '#0D0F1C',
} as const;

// Indigo color palette
export const indigo = {
  25: '#F5F8FF',
  50: '#EEF4FF',
  100: '#E0EAFF',
  200: '#C7D7FE',
  300: '#A4BCFD',
  400: '#8098F9',
  500: '#6172F3',
  600: '#444CE7',
  700: '#3538CD',
  800: '#2D31A6',
  900: '#2D3282',
  950: '#1F235B',
} as const;

// Purple color palette
export const purple = {
  25: '#FAFAFF',
  50: '#F4F3FF',
  100: '#EBE9FE',
  200: '#D9D6FE',
  300: '#BDB4FE',
  400: '#9B8AFB',
  500: '#7A5AF8',
  600: '#6938EF',
  700: '#5925DC',
  800: '#4A1FB8',
  900: '#3E1C96',
  950: '#27115F',
} as const;

// Pink color palette
export const pink = {
  25: '#FEF6FB',
  50: '#FDF2FA',
  100: '#FCE7F6',
  200: '#FCCEEE',
  300: '#FAA7E0',
  400: '#F670C7',
  500: '#EE46BC',
  600: '#DD2590',
  700: '#C11574',
  800: '#9E165F',
  900: '#851651',
  950: '#4E0D30',
} as const;

// Rose color palette
export const rose = {
  25: '#FFF5F6',
  50: '#FFF1F3',
  100: '#FFE4E8',
  200: '#FECDD6',
  300: '#FEA3B4',
  400: '#FD6F8E',
  500: '#F63D68',
  600: '#E31B54',
  700: '#C01048',
  800: '#A11043',
  900: '#89123E',
  950: '#510B24',
} as const;

// Orange color palette
export const orange = {
  25: '#FFFAF5',
  50: '#FFF6ED',
  100: '#FFEAD5',
  200: '#FDDCAB',
  300: '#FEB273',
  400: '#FD853A',
  500: '#FB6514',
  600: '#EC4A0A',
  700: '#C4320A',
  800: '#9C2A10',
  900: '#7E2410',
  950: '#511C10',
} as const;

/**
 * Light Theme Semantic Colors
 * Based on Figma Variables - Light Mode
 */
export const lightTheme = {
  // Background
  bg: {
    primary: white[100], // Main background
    secondary: gray[50], // Secondary background
    tertiary: gray[100], // Tertiary background
    sidebar: white[100], // Sidebar background
    card: white[100], // Card background
    input: white[100], // Input background
    brandSection: brand[950], // Brand section (login page)
  },

  // Text
  text: {
    primary: gray[800], // #1D2939 - Main text
    secondary: gray[500], // #667085 - Secondary text
    tertiary: gray[400], // #98A2B3 - Muted text
    placeholder: gray[400], // #98A2B3 - Placeholder
    disabled: gray[300], // #D0D5DD - Disabled text
    inverse: white[100], // White text on dark bg
    label: gray[700], // #344054 - Form labels
    link: brand[500], // #465FFF - Links
    linkHover: brand[600], // #3641F5 - Link hover
  },

  // Border
  border: {
    primary: gray[200], // #E4E7EC - Default border
    secondary: gray[300], // #D0D5DD - Input border
    focus: brand[300], // #9CB9FF - Focus border
    divider: gray[200], // #E4E7EC - Dividers
  },

  // Brand/Primary
  brand: {
    default: brand[500], // #465FFF
    hover: brand[600], // #3641F5
    active: brand[700], // #2A31D8
    light: brand[50], // #ECF3FF
    lighter: brand[25], // #F2F7FF
  },

  // Status
  status: {
    error: error[500],
    errorLight: error[50],
    errorBorder: error[300],
    warning: warning[500],
    warningLight: warning[50],
    warningBorder: warning[300],
    success: success[500],
    successLight: success[50],
    successBorder: success[300],
    info: blue[500],
    infoLight: blue[50],
    infoBorder: blue[300],
  },
} as const;

/**
 * Dark Theme Semantic Colors
 * Based on Figma Variables - Dark Mode (TailAdmin PRO)
 */
export const darkTheme = {
  // Background
  bg: {
    primary: gray[900], // #101828 - Main background (sidebar/header)
    secondary: gray[800], // #1D2939 - Layout background
    tertiary: gray[700], // #344054 - Tertiary background
    sidebar: gray[900], // #101828 - Sidebar background
    card: gray[800], // #1D2939 - Card background
    input: gray[800], // #1D2939 - Input background
    brandSection: gray[900], // #101828 - Same as container in dark mode
    content: gray[800], // #1D2939 - Content area (slightly lighter than sidebar/header)
  },

  // Text
  text: {
    primary: white[90], // rgba(255,255,255,0.9) - Main text
    secondary: gray[400], // #98A2B3 - Secondary text
    tertiary: gray[500], // #667085 - Muted text
    placeholder: white[30], // rgba(255,255,255,0.3) - Placeholder
    disabled: gray[600], // #475467 - Disabled text
    inverse: gray[900], // Dark text on light bg
    label: gray[400], // #98A2B3 - Form labels
    link: brand[400], // #7592FF - Links (lighter in dark)
    linkHover: brand[300], // #9CB9FF - Link hover
  },

  // Border - TailAdmin PRO dark mode uses subtle borders
  border: {
    primary: white[10], // rgba(255,255,255,0.10) - Subtle border (sidebar/header)
    secondary: gray[700], // #344054 - Input border
    focus: brand[500], // #465FFF - Focus border (brand color)
    divider: white[5], // rgba(255,255,255,0.05) - Very subtle dividers
  },

  // Brand/Primary (same in dark mode)
  brand: {
    default: brand[500], // #465FFF
    hover: brand[600], // #3641F5
    active: brand[700], // #2A31D8
    light: white[10], // 10% white for dark mode badges
    lighter: white[5], // 5% white
  },

  // Status (same colors work in both themes)
  status: {
    error: error[500],
    errorLight: error[900],
    errorBorder: error[700],
    warning: warning[500],
    warningLight: warning[900],
    warningBorder: warning[700],
    success: success[500],
    successLight: success[900],
    successBorder: success[700],
    info: blue[500],
    infoLight: blue[900],
    infoBorder: blue[700],
  },
} as const;

// Legacy colors export for backward compatibility
export const colors = {
  // Palettes
  brand,

  // Light theme as default
  ...lightTheme.brand,
  primary: lightTheme.brand.default,
  primaryHover: lightTheme.brand.hover,
  primaryActive: lightTheme.brand.active,
  primaryLight: lightTheme.brand.light,
  primaryBg: lightTheme.brand.lighter,

  // Text
  textPrimary: lightTheme.text.primary,
  textSecondary: lightTheme.text.secondary,
  textTertiary: lightTheme.text.tertiary,
  textDisabled: lightTheme.text.disabled,
  textInverse: lightTheme.text.inverse,
  textLabel: lightTheme.text.label,

  // Background
  bgPrimary: lightTheme.bg.primary,
  bgSecondary: lightTheme.bg.secondary,
  bgTertiary: lightTheme.bg.tertiary,
  bgDark: darkTheme.bg.primary,
  bgDarkSecondary: darkTheme.bg.secondary,
  bgSidebar: lightTheme.bg.sidebar,
  bgBrandDark: brand[950],

  // Border
  borderPrimary: lightTheme.border.primary,
  borderSecondary: lightTheme.border.secondary,
  borderFocus: lightTheme.border.focus,
  borderFocusDark: darkTheme.border.focus,
  borderInput: lightTheme.border.secondary,

  // Status
  error: lightTheme.status.error,
  errorLight: lightTheme.status.errorLight,
  errorDark: error[700],
  errorBorder: lightTheme.status.errorBorder,

  warning: lightTheme.status.warning,
  warningLight: lightTheme.status.warningLight,
  warningDark: warning[700],
  warningBorder: lightTheme.status.warningBorder,

  success: lightTheme.status.success,
  successLight: lightTheme.status.successLight,
  successDark: success[700],
  successBorder: lightTheme.status.successBorder,

  info: lightTheme.status.info,
  infoLight: lightTheme.status.infoLight,
  infoDark: blue[700],
  infoBorder: lightTheme.status.infoBorder,

  // Theme objects for direct access
  light: lightTheme,
  dark: darkTheme,
} as const;

// All color palettes export
export const colorPalettes = {
  white,
  brand,
  gray,
  error,
  warning,
  success,
  blue,
  blueLight,
  blueGray,
  indigo,
  orange,
  purple,
  pink,
  rose,
} as const;

// Type exports
export type ColorPalette = keyof typeof colorPalettes;
export type ColorShade = 25 | 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900 | 950;
